import * as Sentry from '@sentry/react'

import { ApiError } from '../../types/errors'

export type SentryEnvironment = 'local' | 'ephemeral' | 'staging' | 'production'

const API_ERROR_BODY_MAX_CHARS = 2048
const API_ERROR_BODY_MAX_DEPTH = 5

const redactToShape = (value: unknown, depth: number): unknown => {
  if (depth > API_ERROR_BODY_MAX_DEPTH) {
    return '<truncated>'
  }
  if (value === null) {
    return null
  }
  if (Array.isArray(value)) {
    return value.map((item) => redactToShape(item, depth + 1))
  }
  if (typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, entry]) => [key, redactToShape(entry, depth + 1)]),
    )
  }

  return `<${typeof value}>`
}

const describeApiErrorBody = (body: unknown): { body?: unknown; body_truncated?: boolean } => {
  if (
    body === undefined ||
    (typeof body === 'object' && body !== null && Object.keys(body).length === 0)
  ) {
    return {}
  }

  let shape: unknown
  try {
    shape = redactToShape(body, 0)
  } catch {
    return { body: '<unserializable>' }
  }

  const serialized = JSON.stringify(shape)
  if (serialized.length <= API_ERROR_BODY_MAX_CHARS) {
    return { body: shape }
  }

  return { body: serialized.slice(0, API_ERROR_BODY_MAX_CHARS), body_truncated: true }
}

export const enrichApiErrorEvent = (
  event: Sentry.ErrorEvent,
  hint: Sentry.EventHint,
): Sentry.ErrorEvent => {
  const error = hint.originalException
  if (!(error instanceof ApiError)) {
    return event
  }

  const syntheticBody = Object.fromEntries(
    Object.entries({ code: error.code, details: error.details }).filter(
      ([, value]) => value !== undefined,
    ),
  )

  return {
    ...event,
    tags: { ...event.tags, 'api.status': String(error.status) },
    contexts: {
      ...event.contexts,
      api_error: { status: error.status, ...describeApiErrorBody(syntheticBody) },
    },
  }
}

const TENANT_HOST_PATTERN =
  /\/\/[^./]+(?:\.app)?\.(personalentwicklung\.(?:testing-)?hrworks\.de)/g

const EMAIL_PATTERN = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g

const scrubTenantHost = (value: string): string =>
  value.replace(TENANT_HOST_PATTERN, '//*.$1')

const redactEmails = (value: string): string => value.replace(EMAIL_PATTERN, '[email]')

const scrubUrl = (value: string): string => scrubTenantHost(value).replace(/[?#][\s\S]*$/, '')

const scrubUrlField = (target: Record<string, unknown> | undefined, key: string): void => {
  if (!target) {
    return
  }

  const value = target[key]
  if (typeof value === 'string' && value) {
    target[key] = scrubUrl(value)
  }
}

const URL_DATA_KEYS = ['url', 'from', 'to']
const NAVIGATION_URL_KEYS = ['url', 'fullUrl', 'referrer']

// The element description of a ui.* breadcrumb carries aria-label/title/alt values, which can be names.
const ELEMENT_ATTRIBUTE_PATTERN = /\[[^\]]*\]/g

export const scrubBreadcrumb = (breadcrumb: Sentry.Breadcrumb): Sentry.Breadcrumb => {
  if (breadcrumb.category?.startsWith('ui.') && breadcrumb.message) {
    breadcrumb.message = breadcrumb.message.replace(ELEMENT_ATTRIBUTE_PATTERN, '')
  }

  if (breadcrumb.category === 'console' && breadcrumb.data) {
    delete breadcrumb.data.arguments
  }

  URL_DATA_KEYS.forEach((key) => {
    scrubUrlField(breadcrumb.data, key)
  })

  if (breadcrumb.message) {
    breadcrumb.message = redactEmails(breadcrumb.message)
  }

  return breadcrumb
}

const scrubEvent = (event: Sentry.ErrorEvent): void => {
  scrubUrlField(event.request as Record<string, unknown> | undefined, 'url')
  scrubUrlField(event.request?.headers, 'Referer')

  if (event.request) {
    delete event.request.query_string
  }

  const navigation = event.contexts?.navigation
  NAVIGATION_URL_KEYS.forEach((key) => {
    scrubUrlField(navigation, key)
  })

  if (navigation) {
    delete navigation.search
  }

  if (event.message) {
    event.message = redactEmails(event.message)
  }

  event.exception?.values?.forEach((exception) => {
    if (exception.value) {
      exception.value = redactEmails(exception.value)
    }
  })

  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map((breadcrumb) => scrubBreadcrumb(breadcrumb))
  }
}

export const composeBeforeSend = (
  event: Sentry.ErrorEvent,
  hint: Sentry.EventHint,
): Sentry.ErrorEvent | null => {
  const error = hint.originalException
  if (error instanceof Error) {
    if (error.message.includes('canceled') || error.message.includes('aborted')) {
      return null
    }
  }

  scrubEvent(event)

  return enrichApiErrorEvent(event, hint)
}

export const normalizeEnvironment = (mode: string): SentryEnvironment => {
  if (mode.startsWith('ephemeral')) {
    return 'ephemeral'
  }
  if (mode === 'production') {
    return 'production'
  }
  if (mode === 'staging') {
    return 'staging'
  }

  return 'local'
}

export const shouldInitSentry = (
  environment: SentryEnvironment,
  dsn: string | undefined,
): boolean => environment !== 'local' && Boolean(dsn)

export const initSentry = (): void => {
  const environment = normalizeEnvironment(import.meta.env.MODE)
  const dsn = import.meta.env.VITE_SENTRY_DSN

  if (environment === 'local') {
    return
  }

  if (!shouldInitSentry(environment, dsn)) {
    console.error(`No VITE_SENTRY_DSN for environment "${environment}" — error reporting is disabled`)

    return
  }

  Sentry.init({
    dsn,
    environment,
    release: import.meta.env.VITE_APP_VERSION || undefined,
    debug: import.meta.env.VITE_SENTRY_DEBUG === 'true',
    sendDefaultPii: false,
    beforeSend: composeBeforeSend,
    beforeBreadcrumb: scrubBreadcrumb,
    ignoreErrors: [
      'top.GLOBALS',
      'chrome-extension://',
      'moz-extension://',
      'NetworkError',
      'Network request failed',
      'Failed to fetch',
      'login_required',
    ],
  })
}

/**
 * Capture an exception and send it to Sentry
 */
export const captureException = (
  error: Error,
  context?: Sentry.CaptureContext
): string => {
  return Sentry.captureException(error, context)
}

/**
 * Capture a message and send it to Sentry
 */
export const captureMessage = (
  message: string,
  level?: Sentry.SeverityLevel
): string => {
  return Sentry.captureMessage(message, level)
}

/**
 * Set user context for Sentry
 */
export const setUser = (user: Sentry.User | null): void => {
  Sentry.setUser(user)
}

/**
 * Add breadcrumb for debugging
 */
export const addBreadcrumb = (breadcrumb: Sentry.Breadcrumb): void => {
  Sentry.addBreadcrumb(breadcrumb)
}

/**
 * Set custom context
 */
export const setContext = (name: string, context: Record<string, unknown>): void => {
  Sentry.setContext(name, context)
}

/**
 * Set a tag for filtering in Sentry
 */
export const setTag = (key: string, value: string): void => {
  Sentry.setTag(key, value)
}
